/**
 * SupportView.js
 *
 * Screen asking the diver to contribute a few euros to the project, reached
 * from the settings screen and from the foot of the desktop dashboard.
 *
 * The link is always printed in full next to the button: the desktop build
 * (Tauri) carries no opener plugin, so a target="_blank" there opens nothing,
 * and a diver left with a dead button has no way to reach the page at all.
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { i18n } from '../i18n/index.js'
import { showToast } from '../utils/viewUtils.js'
import { supportTarget } from '../utils/support.js'

export class SupportView {
  constructor(container) {
    this.container = container
    // Resolved once per mount: the platform cannot change under the view
    this.target = supportTarget()
  }

  t(key, params) {
    return i18n.t(key, params)
  }

  render() {
    this.container.innerHTML = `
      <div class="space-y-4 pb-20 max-w-2xl mx-auto">
        ${this.target.mode === 'website' ? this.renderWebsiteCard() : this.renderSupportCard()}
        ${this.renderLinkCard()}
      </div>
    `

    this.bindEvents()
  }

  // Builds where naming a contribution is not allowed: the project website,
  // its documentation and its contacts, and not a word about money
  renderWebsiteCard() {
    return `
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body p-4 space-y-2">
          <h2 class="card-title text-sm">${this.t('support_website_title')}</h2>
          <p class="text-sm">${this.t('support_website_intro')}</p>
        </div>
      </div>
    `
  }

  renderSupportCard() {
    const costs = [
      'support_cost_apple',
      'support_cost_google',
      'support_cost_domain',
      'support_cost_time'
    ]

    return `
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body p-4 space-y-3">
          <h2 class="card-title text-sm">${this.t('support_title')}</h2>
          <p class="text-sm">${this.t('support_intro')}</p>

          <div>
            <h3 class="text-sm font-semibold">${this.t('support_costs_title')}</h3>
            <ul class="list-disc list-inside text-sm opacity-80 mt-1 space-y-1">
              ${costs.map(key => `<li>${this.t(key)}</li>`).join('')}
            </ul>
          </div>

          <p class="text-sm">${this.t('support_amount')}</p>
          <p class="text-sm opacity-80">${this.t('support_nothing_unlocked')}</p>
          <p class="text-sm opacity-80">${this.t('support_thanks')}</p>
        </div>
      </div>
    `
  }

  // Button and printed address, shared by both cards above
  renderLinkCard() {
    const label = this.target.mode === 'website' ? 'support_website_button' : 'support_button'

    return `
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body p-4 space-y-3">
          <a
            href="${this.target.url}"
            target="_blank"
            rel="noopener noreferrer"
            class="btn btn-primary btn-block"
          >${this.t(label)}</a>

          <div class="form-control">
            <label class="label py-1" for="support-url">
              <span class="label-text text-xs">${this.t('support_link_label')}</span>
            </label>
            <div class="flex gap-2">
              <input
                type="text"
                id="support-url"
                class="input input-bordered input-sm flex-1 text-xs"
                value="${this.target.url}"
                readonly
              />
              <button id="support-copy-btn" class="btn btn-outline btn-sm">${this.t('support_copy')}</button>
            </div>
          </div>

          <p class="text-xs opacity-70">${this.t('support_open_hint')}</p>
        </div>
      </div>
    `
  }

  bindEvents() {
    const copyBtn = this.container.querySelector('#support-copy-btn')
    if (!copyBtn) {
      console.error('SupportView.bindEvents: #support-copy-btn is missing, the link can only be selected by hand')
      return
    }

    copyBtn.addEventListener('click', () => this.copyLink())
  }

  async copyLink() {
    const url = this.target.url

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url)
        showToast(this.t('support_copied'))
        return
      }
    } catch (err) {
      // Denied permission, or a page not served over https: fall through to
      // the selection-based copy rather than leaving the diver with nothing
      console.error('SupportView.copyLink: clipboard write failed, trying the selection fallback', err)
    }

    if (this.copyBySelection()) {
      showToast(this.t('support_copied'))
      return
    }

    console.error('SupportView.copyLink: no usable copy path, the address is left for the diver to select')
    showToast(this.t('support_copy_failed'))
  }

  // Older WebViews (and any context without the async clipboard) still honour
  // execCommand on a selected field. The field is the one already on screen,
  // so a failure leaves the address selected and ready to be copied by hand.
  copyBySelection() {
    const input = this.container?.querySelector('#support-url')
    if (!input) {
      console.error('SupportView.copyBySelection: #support-url is missing, nothing to select')
      return false
    }

    try {
      input.focus()
      input.setSelectionRange(0, input.value.length)
      return document.execCommand('copy') === true
    } catch (err) {
      console.error('SupportView.copyBySelection: selection copy failed', err)
      return false
    }
  }

  // No store subscription, no timer and no listener outside the container:
  // the copy handler is released with the container content.
  destroy() {
    this.container = null
    this.target = null
  }
}
